"use client";

// Defer <SceneBackground /> until the KessemLoader fires `kessem:loader-done`.
// Mounting the R3F Canvas, compiling its shader, and animating FloatingShapes
// in parallel with the loader starves the main thread and shows up as jank in
// the liquid fill animation.
//
// We deliberately do NOT prefetch the scene-canvas chunk here. The previous
// eager `void import(...)` downloaded ~240 KB of R3F+three before the loader
// even finished — Lighthouse measured the bytes as 99.97% unused during the
// initial paint window because the code never actually executed within the
// trace. The trade-off is a single short fetch when SceneBackground finally
// mounts, which is masked by the loader fade and only visible in DevTools.

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

// Code-split the entire SceneBackground (which itself dynamic-imports
// scene-canvas). Loaded ONLY when the loader is done, so the bytes never
// compete with the critical path or with the loader animation.
const SceneBackground = dynamic(
  () =>
    import("@/components/site/scene-background").then((m) => ({
      default: m.SceneBackground,
    })),
  { ssr: false, loading: () => null },
);

export function SceneBackgroundGated() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // If the loader already fired before this subscriber mounted (HMR or
    // late hydration), pick up the flag immediately.
    if ((window as unknown as { __kessemLoaderDone?: boolean }).__kessemLoaderDone) {
      setReady(true);
      return;
    }
    const onDone = () => setReady(true);
    window.addEventListener("kessem:loader-done", onDone);
    return () => window.removeEventListener("kessem:loader-done", onDone);
  }, []);

  if (!ready) return null;
  return <SceneBackground />;
}
